import { defineStore } from "pinia";
import type { RouteLocation } from "vue-router";
import store from "@/stores/index";
import { clearTokens } from "@/lib/token-manager";

type State = {
  currentUser: any;
  redirectRoute: Partial<RouteLocation> | null;
  impersonatedCreatorId: string | null;
  adminUserProfile: any | null;
};

type Getters = {
  isAuthenticated(): boolean;
};

type Actions = {
  loadUser(): void;
  clearUser(): void;
  saveRedirectRoute(route: Partial<RouteLocation>): void;
  loadRedirectRoute(): void;
  clearRedirectRoute(): void;
  setImpersonatedCreator(creatorId: string | null): void;
  clearImpersonation(): void;
  setAdminUserProfile(profile: any): void;
  clearAdminUserProfile(): void;
  isAdmin(): boolean;
};

export const useAuthStore = defineStore<"auth", State, Getters, Actions>(
  "auth",
  {
    state() {
      return {
        currentUser: store.state?.user || null,
        redirectRoute: null,
        impersonatedCreatorId:
          localStorage.getItem("impersonatedCreatorId") || null,
        adminUserProfile: JSON.parse(
          localStorage.getItem("adminUserProfile") || "null",
        ),
      };
    },
    getters: {
      isAuthenticated() {
        return !!this.currentUser;
      },
    },
    actions: {
      loadUser() {
        this.currentUser = store.state?.user || null;
      },
      clearUser() {
        clearTokens();
        store.setUser(null);
        this.currentUser = null;
        this.clearImpersonation();
        this.clearAdminUserProfile();
      },
      saveRedirectRoute(route: Partial<RouteLocation>) {
        const { name, params, query, hash } = route;
        localStorage.setItem(
          "redirectRoute",
          JSON.stringify({ name, params, query, hash }),
        );
      },
      loadRedirectRoute() {
        const route = JSON.parse(
          localStorage.getItem("redirectRoute") || "null",
        ) as Partial<RouteLocation> | null;
        this.redirectRoute = route;
      },
      clearRedirectRoute() {
        localStorage.removeItem("redirectRoute");
        this.redirectRoute = null;
      },
      setImpersonatedCreator(creatorId: string | null) {
        this.impersonatedCreatorId = creatorId;
        if (creatorId) {
          localStorage.setItem("impersonatedCreatorId", creatorId);
        } else {
          localStorage.removeItem("impersonatedCreatorId");
        }
      },
      clearImpersonation() {
        this.setImpersonatedCreator(null);
      },
      setAdminUserProfile(profile: any) {
        this.adminUserProfile = profile;
        if (profile) {
          localStorage.setItem("adminUserProfile", JSON.stringify(profile));
        } else {
          localStorage.removeItem("adminUserProfile");
        }
      },
      clearAdminUserProfile() {
        this.adminUserProfile = null;
        localStorage.removeItem("adminUserProfile");
      },
      isAdmin() {
        return store.state?.user?.admin === true;
      },
    },
  },
);
